/**
 * GooglePlacesCombobox — DS Combobox powered by Google Places API.
 *
 * Replaces: MUI Autocomplete + usePlacesService pattern.
 * User types -> predictions load -> user selects -> address parsed.
 * Selection is forced (no freeSolo) — the value must be a real place.
 */
import React, {useState, useEffect, useCallback, useMemo} from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import {Combobox} from '~/src/design-system/BaseComponents'
import type {ComboboxOption} from '~/src/design-system/BaseComponents'
import {extractAddressComponent} from '@utils'
import type {IBaseAddress} from '~/src/models/baseAddress'

export interface GooglePlacesComboboxProps {
  /** Formik field name (or any identifier) */
  name?: string
  /** Current display value */
  value?: string
  /** Called with parsed address when user selects a place */
  onPlaceSelect?: (address: IBaseAddress) => void
  /** Called with raw display value on change */
  onValueChange?: (display: string) => void
  /** When true, searches for cities only; when false, searches full addresses */
  isCityState?: boolean
  /** Placeholder text */
  placeholder?: string
  /** Disabled state */
  disabled?: boolean
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Label */
  label?: string
  /** Error text */
  errorText?: string
  /** Helper text */
  helperText?: string
  /** data-testid */
  'data-testid'?: string
}

type PlacePrediction = {
  description: string
  place_id: string
  terms: Array<{value: string}>
}

type PlaceDetails = {
  adr_address?: string
  geometry?: {location?: {toString(): string}}
}

export function GooglePlacesCombobox({
  value = '',
  onPlaceSelect,
  onValueChange,
  isCityState = false,
  placeholder,
  disabled = false,
  size = 'md',
  label,
  errorText,
  helperText,
  'data-testid': testId,
}: GooglePlacesComboboxProps) {
  const types = isCityState ? ['(cities)'] : ['address']
  const fields = isCityState ? undefined : ['formatted_address', 'street_address', 'name']

  const {placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading} =
    usePlacesService({
      apiKey: import.meta.env.VITE_GOOGLE,
      debounce: 500,
      options: {
        componentRestrictions: {country: 'us'},
        fields,
        strictBounds: false,
        types,
      },
    })

  const [options, setOptions] = useState<ComboboxOption[]>([])

  // Convert predictions to ComboboxOptions
  useEffect(() => {
    if (placePredictions.length > 0) {
      const opts = placePredictions
        .map((p: PlacePrediction) => {
          const optLabel = isCityState
            ? p.terms.length < 2
              ? ''
              : `${p.terms[0]?.value}, ${p.terms[1]?.value}`
            : p.terms.length < 4
            ? ''
            : p.description
          return {value: p.place_id, label: optLabel}
        })
        .filter((o) => o.label !== '')
      setOptions(opts)
    } else {
      setOptions([])
    }
  }, [placePredictions, isCityState])

  const handleSearch = useCallback(
    (query: string) => {
      getPlacePredictions({input: query})
    },
    [getPlacePredictions],
  )

  const handleChange = useCallback(
    (placeId: string | null) => {
      if (!placeId || !placesService) return

      // Find the display label for this selection
      const selectedOpt = options.find((o) => o.value === placeId)
      if (selectedOpt && onValueChange) {
        onValueChange(selectedOpt.label)
      }

      // Fetch place details for structured address
      placesService.getDetails(
        {
          placeId,
          fields: ['adr_address', 'geometry'],
        },
        (placeDetails: PlaceDetails | null) => {
          if (!placeDetails) return
          const {adr_address, geometry} = placeDetails
          const locationStr = geometry?.location?.toString() ?? ''
          const lng = locationStr.split(',')[1]?.split(')')[0] ?? '0'
          const lat = locationStr.split('(')[1]?.split(',')[0] ?? '0'
          // Coordinates must be numbers — the applicant `address.location` has a
          // 2dsphere index that rejects string elements ("Point must only contain
          // numeric elements"), which 500s the applicant save.
          const coordinates = [Number(lng), Number(lat)]
          const location = {type: 'Point', coordinates}

          const address: IBaseAddress = {
            line1: extractAddressComponent('street-address', adr_address ?? ''),
            // SHR-3921: Google occasionally returns the unit as the adr "extended-address"
            // span (subpremise) — capture it to pre-fill the Unit field when present.
            line2: extractAddressComponent('extended-address', adr_address ?? ''),
            city: extractAddressComponent('locality', adr_address ?? ''),
            state: extractAddressComponent('region', adr_address ?? ''),
            zipCode: extractAddressComponent('postal-code', adr_address ?? ''),
            display: selectedOpt?.label ?? '',
            location,
          }

          onPlaceSelect?.(address)
        },
      )
    },
    [placesService, options, onPlaceSelect, onValueChange],
  )

  // `options` holds only live Google predictions, which are empty until the user
  // searches — so a pre-seeded/persisted address (value set on load) matches nothing
  // and the field renders blank. Inject a synthetic option for the current value so
  // the saved address displays; real place_ids still flow through unchanged (STFFLG-3684).
  const optionsWithValue = useMemo(
    () =>
      value && !options.some((o) => o.label === value)
        ? [{value: `__current__:${value}`, label: value}, ...options]
        : options,
    [value, options],
  )
  const selectedPlaceId = optionsWithValue.find((o) => o.label === value)?.value ?? null

  return (
    <div data-testid={testId}>
      <Combobox
        label={label}
        helperText={helperText}
        errorText={errorText}
        options={optionsWithValue}
        value={selectedPlaceId}
        onChange={handleChange}
        onSearch={handleSearch}
        loading={isPlacePredictionsLoading}
        placeholder={placeholder ?? (isCityState ? 'City, State' : '123 Sample Street')}
        disabled={disabled}
        size={size}
      />
    </div>
  )
}

export default GooglePlacesCombobox
